// contractStatusTransitions — locked QLD-aligned state machine for the
// managed contract lifecycle. Single source of truth for which
// transitions are available from a given status, which require a reason
// (audit-defensible early termination / archival), and which are gated
// behind a "More options" disclosure to discourage bypass of the
// meaningful expired/terminated states.

import type { ManagedContractStatus } from "../../types";

export type TransitionGroup = "primary" | "moreOptions";

export interface ContractStatusTransition {
  to: ManagedContractStatus;
  requiresReason: boolean;
  group: TransitionGroup;
  label: string;
}

const TRANSITIONS: Record<ManagedContractStatus, ContractStatusTransition[]> = {
  draft: [
    { to: "active", requiresReason: false, group: "primary", label: "Active" },
    {
      to: "archived",
      requiresReason: true,
      group: "primary",
      label: "Archived",
    },
  ],
  active: [
    {
      to: "expired",
      requiresReason: false,
      group: "primary",
      label: "Expired",
    },
    {
      to: "terminated",
      requiresReason: true,
      group: "primary",
      label: "Terminated",
    },
    {
      to: "archived",
      requiresReason: true,
      group: "moreOptions",
      label: "Archived",
    },
  ],
  expired: [],
  terminated: [],
  archived: [],
};

const TERMINAL_STATUSES: ReadonlySet<ManagedContractStatus> = new Set([
  "expired",
  "terminated",
  "archived",
]);

export function availableTransitions(
  currentStatus: ManagedContractStatus,
): ContractStatusTransition[] {
  return TRANSITIONS[currentStatus] ?? [];
}

export function transitionRequiresReason(
  from: ManagedContractStatus,
  to: ManagedContractStatus,
): boolean {
  const t = availableTransitions(from).find((x) => x.to === to);
  return t?.requiresReason ?? false;
}

export function transitionGroup(
  from: ManagedContractStatus,
  to: ManagedContractStatus,
): TransitionGroup {
  const t = availableTransitions(from).find((x) => x.to === to);
  return t?.group ?? "primary";
}

export function isTerminalStatus(status: ManagedContractStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}
